import React, { useEffect, useMemo, useState } from 'react';
import { useSettings } from '../../contexts/SettingsContext';
import { useBeta } from '../../contexts/BetaContext';
import { GALLERY_PHOTOS } from '../../data/galleryPhotos';
import './GalleryPage.css';

const GalleryPage = () => {
  const { settings } = useSettings();
  const { basePath } = useBeta();
  const [selectedIndex, setSelectedIndex] = useState(null);

  const photos = useMemo(
    () => GALLERY_PHOTOS.filter((photo) => !photo.partyPath || photo.partyPath === basePath),
    [basePath]
  );

  const selectedPhoto = selectedIndex !== null ? photos[selectedIndex] : null;
  const galleryTitle = settings?.partyName || 'Martin-Resnick Oscar Party';

  const handleSelect = (index) => {
    setSelectedIndex(index);
  };

  const handleClose = () => {
    setSelectedIndex(null);
  };

  const handleAdvance = (direction) => {
    if (selectedIndex === null || photos.length === 0) return;
    const nextIndex = (selectedIndex + direction + photos.length) % photos.length;
    setSelectedIndex(nextIndex);
  };

  useEffect(() => {
    if (!selectedPhoto) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      } else if (event.key === 'ArrowLeft') {
        setSelectedIndex((currentIndex) => {
          if (currentIndex === null || photos.length === 0) return currentIndex;
          return (currentIndex - 1 + photos.length) % photos.length;
        });
      } else if (event.key === 'ArrowRight') {
        setSelectedIndex((currentIndex) => {
          if (currentIndex === null || photos.length === 0) return currentIndex;
          return (currentIndex + 1) % photos.length;
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedPhoto, photos.length]);

  return (
    <div className="gallery-page">
      <div className="gallery-page__container">
        <header className="gallery-page__hero">
          <h1 className="gallery-page__title">Photo Gallery</h1>
          <p className="gallery-page__subtitle">
            {galleryTitle}
          </p>
        </header>

        {photos.length === 0 ? (
          <section className="gallery-page__empty" aria-live="polite">
            <h2>No photos added yet</h2>
            <p>
              Upload thumbnails and originals to Firebase Storage, then add entries in
              <code> src/data/galleryPhotos.js</code>.
            </p>
            <div className="gallery-page__empty-card">
              <p className="gallery-page__empty-label">Recommended structure</p>
              <pre className="gallery-page__code">
{`gallery/
  2026/
    thumbs/
    full/`}
              </pre>
            </div>
          </section>
        ) : (
          <section className="gallery-grid" aria-label="Photo gallery">
            {photos.map((photo, index) => (
              <button
                key={photo.id}
                type="button"
                className="gallery-card"
                onClick={() => handleSelect(index)}
              >
                <img
                  className="gallery-card__image"
                  src={photo.thumbnailSrc}
                  alt={photo.alt}
                  loading="lazy"
                  decoding="async"
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                />
                <span className="gallery-card__overlay">
                  <span className="gallery-card__title">{photo.caption || photo.alt}</span>
                  {photo.group && <span className="gallery-card__meta">{photo.group}</span>}
                </span>
              </button>
            ))}
          </section>
        )}
      </div>

      {selectedPhoto && (
        <div
          className="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={selectedPhoto.caption || selectedPhoto.alt}
          onClick={handleClose}
        >
          <div className="gallery-lightbox__frame" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="gallery-lightbox__close"
              onClick={handleClose}
              aria-label="Close photo viewer"
            >
              <span className="gallery-lightbox__icon" aria-hidden="true">×</span>
            </button>
            {photos.length > 1 && (
              <button
                type="button"
                className="gallery-lightbox__nav gallery-lightbox__nav--prev"
                onClick={() => handleAdvance(-1)}
                aria-label="Previous photo"
              >
                <span className="gallery-lightbox__icon gallery-lightbox__icon--nav" aria-hidden="true">‹</span>
              </button>
            )}
            <img
              className="gallery-lightbox__image"
              src={selectedPhoto.src}
              alt={selectedPhoto.alt}
            />
            {photos.length > 1 && (
              <button
                type="button"
                className="gallery-lightbox__nav gallery-lightbox__nav--next"
                onClick={() => handleAdvance(1)}
                aria-label="Next photo"
              >
                <span className="gallery-lightbox__icon gallery-lightbox__icon--nav" aria-hidden="true">›</span>
              </button>
            )}
            <div className="gallery-lightbox__caption">
              <p>{selectedPhoto.caption || selectedPhoto.alt}</p>
              {selectedPhoto.group && <span>{selectedPhoto.group}</span>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
